import { useEffect, useState, type FormEvent } from "react";
import { authFehler, authFehlerArt } from "../konto/auth";
import { Link, Navigate, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { COUNTRIES, isQrIban, isValidBic, isValidIban, passwordHint, passwordLengthProblem } from "@talentkontor/domain";
import { api } from "../../data";
import { Loading } from "../../components/States";
import { toast } from "../../components/Toast";
import { dateOf, useLang, useT } from "./sprache";
import { ChatExportButton, ConfirmDialog, DemoNote, useCreatorAction, useCreatorContext } from "./ui";
import { creatorErrorLine } from "./fehler";
import { savePreferences, saveTalentLanguage } from "./actions";
import { EXPORT_SCOPE, effectiveMailLanguage, languageSummary } from "./model";
import { AppInstallieren } from "../../components/AppInstallieren";
import { DatumsformatWahl } from "../../components/Datumsformat";
import { useOnboardingPrefs } from "./Einfuehrung";
import { rundgangStarten } from "../agentur/coco-rundgang";
import { cocoUiText } from "../agentur/coco-rundgang-texte";
import { ZweiFaktorVerwalten } from "../../components/ZweiFaktor";
import { CaptchaFeld, captchaConfig } from "../konto/Captcha";

/**
 * Konto und Sicherheit (Befunde 3 und 4): Abmelden, App installieren, Passwort und Anmeldeadresse ändern, eigene Daten
 * herunterladen (Art. 15 und 20 DSGVO), Einführung wieder zeigen, Konto löschen lassen. Erreichbar an allen Größen und
 * auch mit archiviertem Profil; dort zusätzlich Bankverbindung für offene Auszahlungen und der bisherige Chatverlauf.
 */
export function Konto() {
  const { ws, lang, account, userId, today } = useCreatorContext();
  const t = useT();
  const email = api.session()?.email ?? account?.session.email ?? "";
  return (
    <main id="main" className="content tk-cr tk-cr-konto">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{t("Dein Creator-Portal", "Your creator portal")}</div>
          <h1>{t("Konto und Sicherheit", "Account and security")}</h1>
          <p>{email ? t(`Angemeldet als ${email}.`, `Signed in as ${email}.`) : null} {t("Dein Konto gilt für alle Agenturen, mit denen du verbunden bist.", "Your account applies to all agencies you are connected with.")}</p>
        </div>
        <div className="row">
          <SignOutButton primary />
        </div>
      </div>

      {ws.archived && <ArchivedPayout talentId={ws.link.talent_id} orgName={ws.org.name} />}
      {ws.archived && (
        <section className="panel stack" aria-labelledby="konto-chat">
          <h2 id="konto-chat">{t("Bisheriger Chatverlauf", "Previous chat history")}</h2>
          <p>{t(`Deine Nachrichten mit ${ws.org.name} als Textdatei, zum Beispiel für Absprachen zu offenen Rechnungen.`, `Your messages with ${ws.org.name} as a text file, for example for arrangements on open invoices.`)}</p>
          <div className="row"><ChatExportButton talentId={ws.link.talent_id} orgName={ws.org.name} /></div>
        </section>
      )}

      <AppInstallieren t={t} />
      {ws.archived ? <SpracheImArchiv /> : <SpracheAnzeige />}
      {userId && <DatumsformatWahl userId={userId} value={account?.dateFormat} today={today} t={t} />}
      <Anmeldung email={email} />
      <ZweiFaktorVerwalten lang={lang} headingId="konto-zwei-faktor" fuer="creator" />
      <DatenHerunterladen />
      {!ws.archived && <EinfuehrungWieder />}
      <KontoLoeschen />
      <DemoNote />
    </main>
  );
}

/**
 * CREATOR-R2-5: Die Sprache der Mails steht je Agentur (talents.language, Benachrichtigungen); hier steht, was wirklich gilt,
 * statt der Portalsprache für alle.
 */
function SpracheAnzeige() {
  const { lang, account } = useCreatorContext();
  const t = useT();
  const links = account?.talentLinks ?? [];
  const q = useQuery({
    queryKey: ["creator", "mail-languages", links.map((l) => l.talent_id).join(",")],
    enabled: links.length > 0,
    queryFn: () => api.select<{ id: string; org_id: string; language: string | null }>("talents", {
      columns: "id, org_id, language", filters: [["id", "in", links.map((l) => l.talent_id)]],
    }),
  });
  const portalPref = account?.language ? lang : null;
  const agencies = (q.data ?? []).flatMap((r) => {
    const org = account?.orgs[r.org_id];
    return org ? [{ orgName: org.name, language: effectiveMailLanguage(r.language, portalPref, org.default_document_language ?? org.locale) }] : [];
  });
  const summary = languageSummary(lang, agencies, lang);
  return (
    <section className="panel stack" aria-labelledby="konto-sprache">
      <h2 id="konto-sprache">{t("Sprache", "Language")}</h2>
      <p>{summary.text} {t("Die Portalsprache änderst du im Profil, die Sprache der Mails je Agentur unter Benachrichtigungen.", "You change the portal language in your profile and the email language per agency under notifications.")}</p>
      <div className="row">
        <Link className="btn" to="/creator/profil#sprache">{t("Zum Profil", "Go to profile")}</Link>
        {!summary.uniform && <Link className="btn" to="/creator/benachrichtigungen">{t("Zu den Benachrichtigungen", "Go to notifications")}</Link>}
      </div>
    </section>
  );
}

/**
 * Sprache auch im Archiv änderbar (SICHT-CREATOR-16): das Profil ist dort geschlossen, die Sprache gilt aber für die Person.
 * Gespeichert wird die Portalsprache (user_preferences) und, soweit die Agentur es zulässt, die Sprache der Mails (talents.language).
 */
function SpracheImArchiv() {
  const { ws, userId, lang } = useCreatorContext();
  const t = useT();
  const { busy, run } = useCreatorAction();
  async function waehle(next: "de" | "en") {
    if (!userId || next === lang) return;
    await run("sprache", async () => {
      const own = await api.select<{ user_id: string }>("user_preferences", { columns: "user_id", filters: [["user_id", "eq", userId]] }).catch(() => []);
      await savePreferences(api, userId, { language: next }, own.length > 0);
      try {
        await saveTalentLanguage(api, [ws.link.talent_id], next);
      } catch {
        /* archiviertes Profil: die Mails dieser Agentur bleiben in ihrer bisherigen Sprache */
      }
    }, next === "en" ? "Language saved." : "Sprache gespeichert.");
  }
  return (
    <section className="panel stack" aria-labelledby="konto-sprache">
      <h2 id="konto-sprache">{t("Sprache", "Language")}</h2>
      <label>
        {t("Sprache des Portals und deiner Mails", "Language of the portal and your emails")}
        <select id="sprache" value={lang} disabled={busy === "sprache"} onChange={(e) => void waehle(e.target.value === "en" ? "en" : "de")}>
          <option value="de">Deutsch</option>
          <option value="en">English</option>
        </select>
      </label>
    </section>
  );
}


/**
 * Abmelden: beendet die Sitzung auf diesem Gerät und führt zur Anmeldung. `everywhere` beendet zusätzlich alle anderen
 * Sitzungen des Kontos (Handy, installierte App), etwa nach einem verlorenen Gerät.
 */
export function SignOutButton({ primary = false, everywhere = false }: { primary?: boolean; everywhere?: boolean }) {
  const t = useT();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  async function signOut() {
    setBusy(true);
    try {
      await api.signOut(everywhere ? { everywhere: true } : undefined);
      navigate("/anmelden", { replace: true });
    } finally {
      setBusy(false);
    }
  }
  return (
    <button type="button" className={primary ? "primary" : undefined} onClick={() => void signOut()} disabled={busy} aria-busy={busy}>
      {busy ? t("Wird abgemeldet …", "Signing out …") : everywhere ? t("Auf allen Geräten abmelden", "Sign out on all devices") : t("Abmelden", "Sign out")}
    </button>
  );
}

/** Weg /creator/abmelden (Befehlspalette): meldet ab und führt zur Anmeldung. */
export function Abmelden() {
  const [done, setDone] = useState(false);
  useEffect(() => {
    let alive = true;
    void api.signOut().finally(() => alive && setDone(true));
    return () => {
      alive = false;
    };
  }, []);
  return done ? <Navigate to="/anmelden" replace /> : <Loading />;
}

/** Passwort und Anmeldeadresse; in der Demo gibt es beides nicht (klarer Hinweis statt Fehler). */
function Anmeldung({ email }: { email: string }) {
  const t = useT();
  if (api.mode === "demo") {
    return (
      <section className="panel stack" aria-labelledby="konto-anmeldung">
        <h2 id="konto-anmeldung">{t("Anmeldung", "Sign-in")}</h2>
        <p>{t("In der Demo gibt es keine Passwörter und keine Bestätigungsmails. Passwort und Anmeldeadresse änderst du in der echten Anwendung hier.",
          "The demo has no passwords and no confirmation emails. In the real application you change your password and sign-in address here.")}</p>
      </section>
    );
  }
  return (
    <section className="panel stack" aria-labelledby="konto-anmeldung">
      <h2 id="konto-anmeldung">{t("Anmeldung", "Sign-in")}</h2>
      <PasswortAendern email={email} />
      {api.updateEmail && <AdresseAendern email={email} />}
      <small>{t("Passwort vergessen? Melde dich ab und nutze auf der Anmeldeseite „Passwort vergessen“.", "Forgot your password? Sign out and use “Forgot password” on the sign-in page.")}</small>
      <div className="row"><SignOutButton everywhere /></div>
      <small>{t("„Abmelden“ beendet nur die Sitzung auf diesem Gerät. „Auf allen Geräten abmelden“ beendet auch die Sitzungen auf Handy, Laptop und in der installierten App.",
        "“Sign out” only ends the session on this device. “Sign out on all devices” also ends the sessions on phone, laptop and in the installed app.")}</small>
    </section>
  );
}

function PasswortAendern({ email }: { email: string }) {
  const t = useT();
  const lang = useLang();
  const { busy, run } = useCreatorAction();
  const [old, setOld] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  // Die erneute Anmeldung mit dem bisherigen Passwort braucht bei eingeschaltetem CAPTCHA ein Token (Befund 02.10.2026)
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaRunde, setCaptchaRunde] = useState(0);
  const mitCaptcha = api.mode !== "demo" && !!captchaConfig();
  function submit(e: FormEvent) {
    e.preventDefault();
    const p = !old ? t("Bitte dein bisheriges Passwort eingeben.", "Please enter your current password.")
      : passwordLengthProblem(next, lang) ? passwordLengthProblem(next, lang)
      : next === old ? t("Das neue Passwort ist dasselbe wie das bisherige.", "The new password is the same as the current one.")
      : again !== next ? t("Die Wiederholung passt nicht zum neuen Passwort.", "The repetition does not match the new password.")
      : mitCaptcha && !captcha ? t("Bitte die Sicherheitsprüfung lösen.", "Please complete the security check.") : null;
    setProblem(p);
    if (p) return;
    void run("passwort", async () => {
      try {
        await api.signInWithPassword(email, old, { captchaToken: captcha });
      } catch (e) {
        // Praxistest K-221: nur falsche Zugangsdaten heißen „bisheriges Passwort stimmt nicht“; Sicherheitsprüfung, Limit usw. im Klartext
        if (authFehlerArt(e) === "invalid_credentials") throw new Error(t("Das bisherige Passwort stimmt nicht.", "The current password is not correct."));
        throw new Error(authFehler(e, lang));
      }
      await api.updatePassword(next);
      return true;
    }, t("Passwort geändert. Andere Geräte sind abgemeldet.", "Password changed. Other devices have been signed out.")).then((ok) => {
      if (mitCaptcha) setCaptchaRunde((n) => n + 1);
      if (!ok) return;
      setOld("");
      setNext("");
      setAgain("");
    });
  }
  return (
    <form className="stack" onSubmit={submit} noValidate aria-busy={busy === "passwort"}>
      <strong>{t("Passwort ändern", "Change password")}</strong>
      <div className="grid">
        <label>{t("Bisheriges Passwort", "Current password")}<input type="password" value={old} onChange={(e) => setOld(e.target.value)} autoComplete="current-password" /></label>
        <label>{t("Neues Passwort", "New password")}<input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" aria-describedby="pw-hinweis" /><small id="pw-hinweis">{passwordHint(lang)}</small></label>
        <label>{t("Neues Passwort wiederholen", "Repeat new password")}<input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" /></label>
      </div>
      {mitCaptcha && <CaptchaFeld onToken={setCaptcha} resetKey={captchaRunde} />}
      {problem && <p className="tk-error" role="alert">{problem}</p>}
      <div className="row"><button type="submit" disabled={!!busy}>{busy === "passwort" ? t("Wird geändert …", "Changing …") : t("Passwort ändern", "Change password")}</button></div>
    </form>
  );
}

function AdresseAendern({ email }: { email: string }) {
  const t = useT();
  const qc = useQueryClient();
  const { busy, run } = useCreatorAction();
  const [value, setValue] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  function submit(e: FormEvent) {
    e.preventDefault();
    const v = value.trim().toLowerCase();
    const p = !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) ? t("Bitte eine gültige E-Mail-Adresse eingeben.", "Please enter a valid email address.")
      : v === email.toLowerCase() ? t("Das ist schon deine Anmeldeadresse.", "This is already your sign-in address.") : null;
    setProblem(p);
    if (p) return;
    void run("adresse", async () => {
      const r = await api.updateEmail!(v);
      setSent(r.needsConfirmation ? v : null);
      await qc.invalidateQueries({ queryKey: ["account"] });
      return true;
    }, t("Anmeldeadresse geändert.", "Sign-in address changed.")).then((ok) => ok && setValue(""));
  }
  return (
    <form className="stack" onSubmit={submit} noValidate aria-busy={busy === "adresse"}>
      <strong>{t("Anmeldeadresse ändern", "Change sign-in address")}</strong>
      <p>{t("Mit dieser Adresse meldest du dich an. Die Adresse für Hinweise deiner Agentur steht getrennt in deinem Profil.",
        "You sign in with this address. The address for notifications from your agency is kept separately in your profile.")}</p>
      <label>{t("Neue E-Mail-Adresse", "New email address")}<input type="email" value={value} onChange={(e) => setValue(e.target.value)} autoComplete="email" /></label>
      {problem && <p className="tk-error" role="alert">{problem}</p>}
      {sent && <div className="notice" role="status">{t(`Wir haben einen Bestätigungslink an ${sent} geschickt. Bis zur Bestätigung gilt die bisherige Adresse.`, `We have sent a confirmation link to ${sent}. Until you confirm, your current address stays valid.`)}</div>}
      <div className="row"><button type="submit" disabled={!!busy}>{busy === "adresse" ? t("Wird gesendet …", "Sending …") : t("Adresse ändern", "Change address")}</button></div>
    </form>
  );
}

/** Alle eigenen Daten als ZIP (Art. 15 und 20 DSGVO): über alle Agenturen, auch frühere. */
function DatenHerunterladen() {
  const t = useT();
  const { lang } = useCreatorContext();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  async function start() {
    setProgress({ done: 0, total: 0 });
    try {
      const { buildDataExport } = await import("./datenExport");
      const r = await buildDataExport(api, lang, (done, total) => setProgress({ done, total }));
      const blob = new Blob([r.zip as BlobPart], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${t("Meine-Daten", "My-data")}_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast(r.missing.length
        ? t(`ZIP gespeichert. ${r.missing.length} Dateien ließen sich nicht laden; sie stehen in LIESMICH.txt.`, `ZIP saved. ${r.missing.length} files could not be downloaded; they are listed in README.txt.`)
        : t("ZIP mit deinen Daten gespeichert.", "ZIP with your data saved."));
    } catch (e) {
      toast(creatorErrorLine(e, lang), "alert");
    } finally {
      setProgress(null);
    }
  }
  return (
    <section className="panel stack" aria-labelledby="konto-daten">
      <h2 id="konto-daten">{t("Deine Daten herunterladen", "Download your data")}</h2>
      <p>{t(`Ein ZIP mit allem, was das Portal über dich als Creator führt: ${EXPORT_SCOPE.de}, dazu die PDFs und Dateien.`,
        `A ZIP with everything the portal holds about you as a creator: ${EXPORT_SCOPE.en}, plus the PDFs and files.`)}</p>
      <div className="row">
        <button type="button" onClick={() => void start()} disabled={!!progress} aria-busy={!!progress}>
          {progress ? (progress.total ? t(`Dateien ${progress.done} von ${progress.total} …`, `Files ${progress.done} of ${progress.total} …`) : t("Wird zusammengestellt …", "Collecting …")) : t("Daten als ZIP herunterladen", "Download data as ZIP")}
        </button>
      </div>
    </section>
  );
}

function EinfuehrungWieder() {
  const t = useT();
  const { userId } = useCreatorContext();
  const { onboarding, save } = useOnboardingPrefs(userId);
  const { busy, run } = useCreatorAction();
  const off = !!onboarding.intro_off;
  return (
    <section className="panel stack" aria-labelledby="konto-einfuehrung">
      <h2 id="konto-einfuehrung">{t("Einführung und Hinweise", "Introduction and tips")}</h2>
      <p>{off
        ? t("Die kurze Einführung je Seite ist ausgeschaltet.", "The short introduction per page is switched off.")
        : t("Beim ersten Besuch einer Seite erklärt eine kurze Karte, was du dort findest.", "On your first visit to a page, a short card explains what you will find there.")}</p>
      <div className="row">
        <button type="button" disabled={!!busy || !userId} onClick={() => void run("einfuehrung", () => save({ guides: {}, intro_off: false, first_steps_dismissed: false }),
          t("Einführung und Erste Schritte erscheinen wieder.", "Introduction and first steps will show again."))}>
          {t("Einführung wieder anzeigen", "Show introduction again")}
        </button>
        <button type="button" onClick={() => rundgangStarten("creator")}>{cocoUiText(t).rundgangStarten}</button>
        {!off && (
          <button type="button" disabled={!!busy || !userId} onClick={() => void run("einfuehrung", () => save({ intro_off: true }), t("Einführung ausgeschaltet.", "Introduction switched off."))}>
            {t("Einführung ausschalten", "Switch off introduction")}
          </button>
        )}
      </div>
    </section>
  );
}

/** Offene Posten je Agentur vor dem Löschantrag (account_delete_preview, 0086). */
export interface OpenItems {
  agency: string;
  org_id: string;
  payouts: number;
  payout_numbers: string[];
  contracts_to_sign: number;
  agency_invoices: number;
}

/** CREATOR-R2-3: Je Agentur ein Satz mit dem, was noch offen ist; Agenturen ohne offene Posten fehlen. */
export function openItemLines(items: readonly OpenItems[], t: (de: string, en: string) => string): string[] {
  return items.flatMap((x) => {
    // CREATOR-R3-9: die Rechnungsnummern gehören an die Auszahlungen, nicht ans Satzende hinter die Agenturrechnung
    const numbers = x.payout_numbers.length ? ` (${x.payout_numbers.join(", ")})` : "";
    const parts = [
      x.payouts === 1 ? t(`1 offene Auszahlung${numbers}`, `1 open payout${numbers}`) : x.payouts > 1 ? t(`${x.payouts} offene Auszahlungen${numbers}`, `${x.payouts} open payouts${numbers}`) : null,
      x.contracts_to_sign === 1 ? t("1 Vertrag zur Unterschrift", "1 contract to sign") : x.contracts_to_sign > 1 ? t(`${x.contracts_to_sign} Verträge zur Unterschrift`, `${x.contracts_to_sign} contracts to sign`) : null,
      x.agency_invoices === 1 ? t("1 offene Rechnung deiner Agentur", "1 open invoice from your agency") : x.agency_invoices > 1 ? t(`${x.agency_invoices} offene Rechnungen deiner Agentur`, `${x.agency_invoices} open invoices from your agency`) : null,
    ].filter(Boolean);
    if (!parts.length) return [];
    return [`${x.agency}: ${parts.join(", ")}`];
  });
}

function KontoLoeschen() {
  const t = useT();
  const navigate = useNavigate();
  const { busy, run } = useCreatorAction();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const preview = useQuery({ queryKey: ["creator", "account-delete-preview"], enabled: open, queryFn: () => api.read<OpenItems[]>("account_delete_preview", {}) });
  const lines = openItemLines(preview.data ?? [], t);
  return (
    <section className="panel stack" aria-labelledby="konto-loeschen">
      <h2 id="konto-loeschen">{t("Konto löschen lassen", "Request account deletion")}</h2>
      <p>{t("Dein Zugang zu allen Agenturen endet sofort, auch zu Rechnungen und Verträgen früherer Agenturen; das Anmeldekonto löscht der Plattform-Betrieb danach endgültig. Rechnungen, Verträge und Nachrichten muss deine Agentur gesetzlich aufbewahren; sie bleiben dort. Lade deine Daten vorher herunter.",
        "Your access to all agencies ends immediately, including invoices and contracts of former agencies; the platform operator then deletes the sign-in account permanently. Your agency must keep invoices, contracts and messages by law; they remain there. Download your data first.")}</p>
      <div className="row"><button type="button" className="danger" onClick={() => setOpen(true)}>{t("Konto löschen lassen", "Request account deletion")}</button></div>
      <ConfirmDialog open={open} title={t("Konto löschen lassen?", "Request account deletion?")} danger busy={busy === "loeschen"} disabled={!confirmed}
        confirm={t("Konto löschen lassen", "Request account deletion")}
        onClose={() => setOpen(false)}
        onConfirm={() => void run("loeschen", async () => {
          await api.rpc("account_delete_request", { p_reason: reason.trim() || null });
          await api.signOut({ everywhere: true });
          navigate("/anmelden", { replace: true });
          return true;
        }, t("Löschung beantragt. Du bist abgemeldet.", "Deletion requested. You are signed out."))}
        text={t("Der Zugang endet sofort und lässt sich nicht selbst wiederherstellen. Deine Agenturen bekommen eine Nachricht, dass du das Portal verlassen hast.", "Access ends immediately and cannot be restored by yourself. Your agencies are notified that you have left the portal.")}>
        {lines.length > 0 && (
          <div className="notice stack tk-gap-sm" role="status">
            <p><strong>{t("Noch offen:", "Still open:")}</strong></p>
            <ul>{lines.map((l) => <li key={l}>{l}</li>)}</ul>
            <p>{t("Auszahlungen macht deine Agentur weiter; Nachfragen, Zahlungsmeldungen und Unterschriften im Portal gehen danach nicht mehr. Kläre das vorher mit ihr.",
              "Your agency still makes payouts; questions, payment reports and signatures in the portal are no longer possible afterwards. Settle this with them first.")}</p>
          </div>
        )}
        <label>
          {t("Grund (freiwillig)", "Reason (optional)")}
          <textarea rows={2} maxLength={400} value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
        <label className="row tk-check">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          {t("Ich möchte mein Konto löschen lassen.", "I want my account to be deleted.")}
        </label>
      </ConfirmDialog>
    </section>
  );
}

// ── Archiv: Bankverbindung für offene Auszahlungen (Agenturwechsel) ──────────

interface ArchivedStatus {
  open: number;
  target: string | null;
  method: string;
  updated_at: string | null;
}

function ArchivedPayout({ talentId, orgName }: { talentId: string; orgName: string }) {
  const t = useT();
  const { lang } = useCreatorContext();
  const q = useQuery({ queryKey: ["creator", talentId, "archived-payout"], queryFn: () => api.read<ArchivedStatus>("archived_payout_status", { p_talent: talentId }) });
  const { busy, run } = useCreatorAction();
  const [method, setMethod] = useState<"sepa" | "swift" | "paypal" | "wise">("sepa");
  const [f, setF] = useState({ holder: "", iban: "", bic: "", bank: "", country: "", account: "", routing: "", mail: "" });
  const [problem, setProblem] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const countries = [...COUNTRIES].sort((a, c) => a.name[lang].localeCompare(c.name[lang], lang));
  if (q.isPending) return null;
  if (q.error || !q.data) return null;
  const s = q.data;
  const mailWay = method === "paypal" || method === "wise";

  function submit(e: FormEvent) {
    e.preventDefault();
    const p = mailWay
      ? (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.mail.trim()) ? t("Bitte eine gültige E-Mail-Adresse für die Auszahlung angeben.", "Please enter a valid email address for the payout.") : null)
      : !f.holder.trim() ? t("Bitte den Kontoinhaber angeben.", "Please enter the account holder.")
      : f.iban.trim() && !isValidIban(f.iban) ? t("Die IBAN ist ungültig (Prüfziffer stimmt nicht).", "The IBAN is invalid (check digits do not match).")
      : f.iban.trim() && isQrIban(f.iban) ? t("Das ist eine QR-IBAN. Sie nimmt nur Zahlungen mit QR-Referenz an, keine Überweisungen. Bitte die normale IBAN deines Kontos eintragen.", "This is a QR-IBAN. It only accepts payments with a QR reference, not transfers. Please enter the normal IBAN of your account.")
      : !f.iban.trim() && !(method === "swift" && f.account.trim()) ? t("Bitte eine IBAN angeben.", "Please enter an IBAN.")
      : f.bic.trim() && !isValidBic(f.bic) ? t("Die BIC hat nicht das übliche Format (8 oder 11 Zeichen).", "The BIC does not have the usual format (8 or 11 characters).") : null;
    setProblem(p);
    if (p) return;
    void run("bank", () => api.rpc("archived_payout_update", {
      p_talent: talentId,
      p_details: { payout_method: method, bank_account_holder: f.holder, iban: f.iban, bic: f.bic, bank_name: f.bank, bank_country: f.country, account_number: f.account, routing_code: f.routing, payout_email: f.mail },
    }), t(`Neue Bankverbindung gespeichert. ${orgName} prüft sie vor der nächsten Auszahlung; du bekommst eine Mail.`, `New bank details saved. ${orgName} checks them before the next payout; you will receive an email.`))
      .then((ok) => ok && setF({ holder: "", iban: "", bic: "", bank: "", country: "", account: "", routing: "", mail: "" }));
  }

  return (
    <section className="panel stack" aria-labelledby="konto-bank">
      <h2 id="konto-bank">{t("Bankverbindung für offene Auszahlungen", "Bank details for open payouts")}</h2>
      {s.open === 0 ? (
        <p>{t(`Bei ${orgName} ist keine Auszahlung mehr offen. Eine Änderung der Bankverbindung ist hier nicht mehr nötig.`, `No payout is open at ${orgName} any more. Changing the bank details is no longer needed here.`)}</p>
      ) : (
        <>
          <p>{t(`${s.open === 1 ? "1 Auszahlung ist" : `${s.open} Auszahlungen sind`} bei ${orgName} noch offen. Hat sich dein Konto geändert, trag das neue hier ein; deine Agentur prüft es vor der nächsten Auszahlung.`,
            `${s.open === 1 ? "1 payout is" : `${s.open} payouts are`} still open at ${orgName}. If your account has changed, enter the new one here; your agency checks it before the next payout.`)}</p>
          <p>{t("Hinterlegt:", "On file:")} <strong>{s.target ?? t("keine Angabe", "none")}</strong>{s.updated_at ? t(` · geändert am ${dateOf(s.updated_at, lang)}`, ` · changed on ${dateOf(s.updated_at, lang)}`) : ""}</p>
          <form className="stack" onSubmit={submit} noValidate aria-busy={busy === "bank"}>
            <label>
              {t("Auszahlungsweg", "Payout method")}
              <select value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
                <option value="sepa">{t("Überweisung (SEPA, IBAN)", "Bank transfer (SEPA, IBAN)")}</option>
                <option value="swift">{t("Auslandsüberweisung (SWIFT)", "International transfer (SWIFT)")}</option>
                <option value="paypal">PayPal</option>
                <option value="wise">Wise</option>
              </select>
            </label>
            {mailWay ? (
              <label>{t("E-Mail-Adresse für die Auszahlung", "Email address for the payout")}<input type="email" value={f.mail} onChange={set("mail")} autoComplete="email" /></label>
            ) : (
              <div className="grid">
                <label>{t("Kontoinhaber", "Account holder")}<input value={f.holder} onChange={set("holder")} /></label>
                <label>IBAN<input value={f.iban} onChange={set("iban")} autoComplete="off" /></label>
                {method === "swift" && <label>{t("Kontonummer (ohne IBAN)", "Account number (without IBAN)")}<input value={f.account} onChange={set("account")} autoComplete="off" /></label>}
                {method === "swift" && <label>{t("Bankleitzahl / Routing", "Bank code / routing")}<input value={f.routing} onChange={set("routing")} autoComplete="off" /></label>}
                <label>BIC<input value={f.bic} onChange={set("bic")} autoComplete="off" /></label>
                <label>{t("Bank", "Bank")}<input value={f.bank} onChange={set("bank")} /></label>
                <label>
                  {t("Bankland", "Bank country")}
                  <select value={f.country} onChange={set("country")}>
                    <option value="">{t("Bitte wählen", "Please choose")}</option>
                    {countries.map((c) => <option key={c.code} value={c.code}>{c.name[lang]}</option>)}
                  </select>
                </label>
              </div>
            )}
            {problem && <p className="tk-error" role="alert">{problem}</p>}
            <div className="row"><button type="submit" className="primary" disabled={!!busy}>{busy === "bank" ? t("Wird gespeichert …", "Saving …") : t("Neue Bankverbindung speichern", "Save new bank details")}</button></div>
          </form>
        </>
      )}
    </section>
  );
}
